import React, { useState, useEffect } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import OTPDialog from '../Auth/OTPDialog';
import { useAuth } from '../../api/auth/store';
import Loading from '../../components/Loading';
import { requestPasswordChangeOTP, verifyPasswordChangeOTP, confirmPasswordChange } from '../../api/auth/actions';
import { btoa } from '../../utils/base64';
import useUXStore from '../../api/ux/store';
import { redirectToLogin } from '../../utils/functions';
import { validatePasswordComplexity } from '../../utils/validation';
import PasswordStrengthMeter from '../../components/PasswordStrengthMeter';

interface ChangePasswordProps {
  dialogId: string;
}

type Step = 'request' | 'verify' | 'change' | 'success';

const ChangePassword: React.FC<ChangePasswordProps> = ({ dialogId }) => {
  const [step, setStep] = useState<Step>('request');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  // Password form fields
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  const { signed } = useAuth(state => state);
  const { refreshDialog, hideDialog, addToast } = useUXStore(state => state);

  // Countdown effect for success step
  useEffect(() => {
    if (step === 'success' && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(redirectCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (step === 'success' && redirectCountdown === 0) {
      hideDialog(dialogId);
      redirectToLogin();
    }
  }, [step, redirectCountdown, dialogId, hideDialog]);

  // Update dialog based on current step
  useEffect(() => {
    let title = 'Change Password';
    let confirmContent = 'Continue';
    let confirmActive = true;

    switch (step) {
      case 'request':
        title = 'Change Password';
        confirmContent = 'Send Verification Code';
        break;
      case 'verify':
        title = 'Verify Your Identity';
        confirmContent = undefined as any; // No footer for OTP step
        break;
      case 'change':
        title = 'Enter New Password';
        confirmContent = undefined as any; // No footer for change step - button in form
        break;
      case 'success':
        title = 'Password Changed Successfully';
        confirmContent = undefined as any; // No footer for success
        break;
    }

    refreshDialog({
      id: dialogId,
      title,
      visible: true,
      confirmContent,
      confirmActive: confirmActive && !loading,
      onConfirm: confirmContent ? handleStepAction : undefined,
      onClose: handleCancel,
      className: 'modal-md'
    });
  }, [step, loading, dialogId]);

  const handleCancel = () => {
    // Reset state when closing
    setStep('request');
    setError('');
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setVerificationToken('');
    setRedirectCountdown(3);
    hideDialog(dialogId);
  };

  const handleStepAction = () => {
    switch (step) {
      case 'request':
        handleRequestOTP();
        break;
      // 'change' step now has button inside the form
    }
  };

  const handleRequestOTP = async () => {
    setLoading(true);
    setError('');

    try {
      await requestPasswordChangeOTP();
      setStep('verify');
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (otp: string) => {
    try {
      const token = await verifyPasswordChangeOTP(otp);
      setVerificationToken(token);
      setStep('change');
    } catch (err: any) {
      throw new Error(err.message || 'Invalid OTP code');
    }
  };

  const handleResendOTP = async () => {
    await requestPasswordChangeOTP();
  };

  const validatePasswords = (): string | null => {
    console.log('Validating passwords:', { oldPassword, newPassword, confirmPassword });
    if (oldPassword === "" || newPassword === "" || confirmPassword === "") {
      return 'All fields are required';
    }

    const complexityError = validatePasswordComplexity(newPassword);
    if (complexityError) {
      return complexityError;
    }

    if (newPassword === oldPassword) {
      return 'New password must be different from old password';
    }

    if (newPassword !== confirmPassword) {
      return 'Passwords do not match';
    }

    return null;
  };

  const handleChangePassword = async () => {
    const validationError = validatePasswords();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Base64 encode passwords before sending
      const encodedOldPassword = btoa(oldPassword);
      const encodedNewPassword = btoa(newPassword);

      await confirmPasswordChange(
        encodedOldPassword,
        encodedNewPassword,
        encodedNewPassword, // confirm password
        verificationToken
      );

      setStep('success');
      setRedirectCountdown(3); // Start countdown
      addToast('Success', 'Password changed successfully. Please login again.', null, 'success');
    } catch (err: any) {
      setError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 'request':
        return (
          <>
            <p>
              To change your password, we'll need to verify your identity by sending
              a verification code to your registered email address.
            </p>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading && (
              <div className="text-center my-3">
                <Loading />
              </div>
            )}
          </>
        );

      case 'verify':
        return (
          <OTPDialog
            mode="page"
            title=""
            description="Enter the 6-digit code sent to your email"
            onVerify={handleVerifyOTP}
            onResend={handleResendOTP}
            onCancel={handleCancel}
            autoFocus={true}
            length={6}
            resendCooldown={60}
            expirationTime={300} // 5 minutes = 300 seconds
          />
        );

      case 'change':
        return (
          <Form onSubmit={(e) => { e.preventDefault(); handleChangePassword(); }}>
            <Form.Group className="mb-3">
              <Form.Label>Current Password</Form.Label>
              <Form.Control
                type={showPasswords ? 'text' : 'password'}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="Enter your current password"
                required
                autoComplete="current-password"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type={showPasswords ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                required
                autoComplete="new-password"
              />
              <Form.Text className="text-muted">
                Must be at least 8 characters long and include uppercase, lowercase, and number
              </Form.Text>
              <PasswordStrengthMeter password={newPassword} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type={showPasswords ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                required
                autoComplete="new-password"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Show passwords"
                checked={showPasswords}
                onChange={(e) => setShowPasswords(e.target.checked)}
              />
            </Form.Group>

            {error && <Alert variant="danger">{error}</Alert>}

            <div className="d-flex justify-content-end gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? <Loading /> : 'Change Password'}
              </Button>
            </div>
          </Form>
        );

      case 'success':
        return (
          <div className="text-center">
            <h4 className="mb-4 text-success">✓ Password Changed Successfully</h4>
            <p>
              Your password has been changed successfully. All your sessions have been
              invalidated for security reasons.
            </p>
            <p className="mb-3">
              <strong>Redirecting to login in {redirectCountdown}...</strong>
            </p>
            <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#0d6efd' }}>
              {redirectCountdown}
            </div>
          </div>
        );
    }
  };

  return renderStep();
};

export default ChangePassword;